/**
 * The Verdict (PRD §1.9, ARCH §2.2).
 *
 * RULE-003: "Every screen leads with a plain-language sentence, not a number.
 * *'Worth calling today'* above the reason, the evidence beneath, and the score
 * present but not the headline. This holds on the lead detail, the leads list,
 * the notification subject line, the calibration bench, the dashboard and the
 * outcome page. **If a screen leads with a figure and leaves the reader to
 * interpret it, the direction has been abandoned.**"
 *
 * Composed once, here, at scoring time — and stored on `leads.verdict_sentence`
 * and `leads.verdict_reason`. ARCH §3.3 denormalises them on purpose: the same
 * two strings render on the list, the dashboard, the drawer and a notification
 * subject line, and recomputing per row would re-read the rubric.
 *
 * PRD §1.9 also sets this module's ceiling: "The Verdict composes
 * deterministically at M1 from the band plus the two top-contributing
 * criteria... The M2 model writes a better sentence into a slot that already
 * works." So the register below is plainer than the mockups' demo prose by
 * design, and the slot is what has to be right.
 */
import { ARCHETYPES, type Archetype } from './archetype'
import { BAND_TONES, type Band, type BandTone } from './rubric'
import type { Contribution } from './scoring'

export type Verdict = { sentence: string; reason: string }

/* ── the sentence ───────────────────────────────────────────────────────── */

/**
 * A sentence for every band tone × every archetype.
 *
 * Keyed on the **tone**, not the band, because a band's label is the
 * operator's ("Hot", "Exceptional", "Not right now") while the ladder beneath
 * it is fixed — RULE-002's nurture → review → hot. An operator renaming a band
 * cannot leave a combination without a sentence.
 *
 * The three `qualifier` sentences are verbatim from the mockups:
 * `leads-list` and `lead-detail` render *"Worth calling today."*,
 * `editor-qualify`'s probe renders *"Worth a look."*, and `leads-list`'s
 * lowest row renders *"Send the guide."* — which is also what `editor-logic`
 * promises a disqualified respondent sees. The other twelve hold that register
 * against PRD §3.2's "Outcome shows" column and are Q-010, awaiting copy
 * review; no mockup covers them.
 */
export const VERDICT_SENTENCES: Record<Archetype, Record<BandTone, string>> = {
  qualifier: {
    hot: 'Worth calling today.',
    review: 'Worth a look.',
    nurture: 'Send the guide.',
  },
  scorecard: {
    hot: 'Ahead of most who take this.',
    review: 'Solid, with room to move.',
    nurture: 'Plenty of ground to make up.',
  },
  recommender: {
    hot: 'Ready for the full package.',
    review: 'The middle tier fits.',
    nurture: 'Start with the basics.',
  },
  triage: {
    hot: 'Send this straight to the team.',
    review: 'Queue this for a reader.',
    nurture: 'This one can wait.',
  },
  eligibility: {
    hot: 'Eligible, on every count.',
    review: 'Eligible, with one thing to check.',
    nurture: 'Not eligible yet.',
  },
}

/* ── the colour of a band ───────────────────────────────────────────────── */

/**
 * Story 2.3 AC: "Nurture, review and hot map to `--mdx-nurture`,
 * `--mdx-review`, `--mdx-hot`; `--mdx-error` is never used for a band,
 * including the lowest one."
 *
 * These are token NAMES, never values — CLAUDE.md forbids a colour literal
 * anywhere but `design/tokens.css`, and `tests/band-tones.test.ts` checks each
 * name is defined there. RULE-002's "no red" is structural here: the ladder has
 * three tones and `error` is not one of them, so there is no band a red token
 * could be attached to.
 */
export const BAND_TONE_TOKEN: Record<BandTone, string> = {
  nurture: '--mdx-nurture',
  review: '--mdx-review',
  hot: '--mdx-hot',
}

/* ── evidence strength (DECISION-026) ───────────────────────────────────── */

export const EVIDENCE_STRENGTHS = ['met', 'partial', 'weak', 'absent'] as const
export type EvidenceStrength = (typeof EVIDENCE_STRENGTHS)[number]

/**
 * DECISION-026: "the glyph is a pure function of `points_awarded ÷
 * points_max`: ≥ 0.85 → met, ≥ 0.4 → partial, > 0 → weak, 0 or unanswered →
 * absent. Fixed, not authorable. One function serves the lead drawer and the
 * calibration probe, so the two cannot disagree."
 *
 * That "one function" is this one, and the Verdict's reason reads it too — so
 * a row saying *"budget partly met"* and a drawer glyph saying *met* about the
 * same criterion is not a thing that can happen.
 */
export function evidenceStrength(pointsAwarded: number, pointsMax: number): EvidenceStrength {
  if (pointsMax <= 0 || pointsAwarded <= 0) return 'absent'
  const ratio = pointsAwarded / pointsMax
  if (ratio >= 0.85) return 'met'
  if (ratio >= 0.4) return 'partial'
  return 'weak'
}

/** How each strength reads in the reason line. Suffixed rather than prefixed
 *  because a criterion label may be a noun ("Budget") or a verb phrase ("Can
 *  sign"), and only a suffix reads as English after both. */
const STRENGTH_PHRASE: Record<EvidenceStrength, string> = {
  met: 'met',
  partial: 'partly met',
  weak: 'barely met',
  absent: 'not met',
}

/* ── the composer ───────────────────────────────────────────────────────── */

export type VerdictOptions = {
  /** Set when a disqualifier fired. The gate is the reason — the contribution
   *  list is empty in that case, because no score was calculated. */
  disqualifiedReason?: string | null
}

/**
 * Band + the two top-contributing criteria → the sentence and its reason.
 *
 * There is no branch that returns a number, and no fallback that returns one
 * either: an archetype and a tone are both closed enums, so the sentence table
 * is total by construction rather than by a default case.
 */
export function verdict(
  band: Band,
  contributions: Contribution[],
  archetype: Archetype,
  options: VerdictOptions = {},
): Verdict {
  const sentence = VERDICT_SENTENCES[archetype][band.tone]

  if (options.disqualifiedReason) {
    return { sentence, reason: options.disqualifiedReason }
  }

  return { sentence, reason: reasonFrom(contributions) }
}

/**
 * The two top contributors, named with how well each was met.
 *
 * Ties keep rubric order: `sort` is stable, so two criteria awarding the same
 * points appear in the order the operator wrote them rather than in whatever
 * order the engine happened to produce.
 */
export function reasonFrom(contributions: Contribution[]): string {
  return [...contributions]
    .sort((a, b) => b.points_awarded - a.points_awarded)
    .slice(0, 2)
    .map(
      (contribution) =>
        `${contribution.criterion_label.toLowerCase()} ${STRENGTH_PHRASE[evidenceStrength(contribution.points_awarded, contribution.points_max)]}`,
    )
    .join(', ')
}

/** Every band tone × archetype pair, for the exhaustive test and for anything
 *  that needs to enumerate the copy surface. */
export const verdictCombinations = (): { archetype: Archetype; tone: BandTone }[] =>
  ARCHETYPES.flatMap((archetype) => BAND_TONES.map((tone) => ({ archetype, tone })))
